import React, { useEffect, useState } from 'react';
import './Deposit.css';
import { authService } from '../services/authService';

const BankDetails = () => {
  const [form, setForm] = useState({
    bank_name: '',
    branch_address: '',
    ac_holder_name: '',
    ac_number: '',
    ifsc_code: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = authService.getToken();

  /* =========================
     VALIDATION FUNCTIONS
  ========================= */
  const validateBankName = (value) => {
    if (!value.trim()) return 'Bank name is required';
    if (value.length < 2) return 'Bank name is too short';
    if (value.length > 100) return 'Bank name cannot exceed 100 characters';
    if (!/^[A-Za-z\s.\-&]+$/.test(value)) return 'Only letters, spaces, dots, hyphens and & allowed';
    return '';
  };

  const validateBranchAddress = (value) => {
    if (!value.trim()) return 'Branch address is required';
    if (value.length < 5) return 'Branch address is too short';
    if (value.length > 200) return 'Branch address cannot exceed 200 characters';
    if (!/^[A-Za-z0-9\s.,\-/()#&]+$/.test(value)) return 'Invalid characters in address';
    return '';
  };

  const validateAccountHolderName = (value) => {
    if (!value.trim()) return 'Account holder name is required';
    if (value.length < 3) return 'Name is too short';
    if (value.length > 100) return 'Name cannot exceed 100 characters';
    if (!/^[A-Za-z\s.'-]+$/.test(value)) return 'Only letters, spaces, apostrophes, hyphens and dots allowed';
    if (/^\s+|\s+$/.test(value)) return 'Name cannot start or end with spaces';
    if (/\s{2,}/.test(value)) return 'Multiple consecutive spaces are not allowed';
    return '';
  };

  const validateAccountNumber = (value) => {
    if (!value.trim()) return 'Account number is required';
    if (value.length < 9) return 'Account number is too short (min 9 digits)';
    if (value.length > 18) return 'Account number cannot exceed 18 digits';
    if (!/^\d+$/.test(value)) return 'Only numbers allowed';
    
    // Indian account number validation (typically 9-18 digits)
    const accountNum = parseInt(value, 10);
    if (isNaN(accountNum)) return 'Invalid account number';
    if (accountNum < 0) return 'Account number cannot be negative';
    
    return '';
  };

  const validateIFSC = (value) => {
    if (!value.trim()) return 'IFSC code is required';
    const ifsc = value.toUpperCase();
    
    // IFSC format: 11 characters, first 4 letters, 5th is 0, last 6 alphanumeric
    if (ifsc.length !== 11) return 'IFSC must be 11 characters';
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) return 'Invalid IFSC format (e.g., SBIN0000123)';
    
    return '';
  };

  const validateAll = () => {
    const newErrors = {};
    
    const bankError = validateBankName(form.bank_name);
    const branchError = validateBranchAddress(form.branch_address);
    const holderError = validateAccountHolderName(form.ac_holder_name);
    const accountError = validateAccountNumber(form.ac_number);
    const ifscError = validateIFSC(form.ifsc_code);
    
    if (bankError) newErrors.bank_name = bankError;
    if (branchError) newErrors.branch_address = branchError;
    if (holderError) newErrors.ac_holder_name = holderError;
    if (accountError) newErrors.ac_number = accountError;
    if (ifscError) newErrors.ifsc_code = ifscError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     INPUT HANDLERS WITH VALIDATION
  ========================= */
  const handleBankNameChange = (e) => {
    const value = e.target.value;
    // Allow letters, spaces, dots, hyphens, and &
    if (!/^[A-Za-z\s.\-&]*$/.test(value)) return;
    if (value.length > 100) return;
    
    setForm(prev => ({ ...prev, bank_name: value }));
    if (errors.bank_name) setErrors(prev => ({ ...prev, bank_name: '' }));
  };

  const handleBranchAddressChange = (e) => {
    const value = e.target.value;
    // Allow alphanumeric, spaces, and common address characters
    if (!/^[A-Za-z0-9\s.,\-/()#&]*$/.test(value)) return;
    if (value.length > 200) return;
    
    setForm(prev => ({ ...prev, branch_address: value }));
    if (errors.branch_address) setErrors(prev => ({ ...prev, branch_address: '' }));
  };

  const handleAccountHolderChange = (e) => {
    const value = e.target.value;
    // Allow letters, spaces, apostrophes, hyphens, and dots
    if (!/^[A-Za-z\s.'-]*$/.test(value)) return;
    if (value.length > 100) return;
    
    setForm(prev => ({ ...prev, ac_holder_name: value }));
    if (errors.ac_holder_name) setErrors(prev => ({ ...prev, ac_holder_name: '' }));
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 18) return;
    
    setForm(prev => ({ ...prev, ac_number: value }));
    if (errors.ac_number) setErrors(prev => ({ ...prev, ac_number: '' }));
  };

  const handleIFSCChange = (e) => {
    const value = e.target.value.toUpperCase();
    // Allow only uppercase letters and numbers
    if (!/^[A-Z0-9]*$/.test(value)) return;
    if (value.length > 11) return;
    
    setForm(prev => ({ ...prev, ifsc_code: value }));
    if (errors.ifsc_code) setErrors(prev => ({ ...prev, ifsc_code: '' }));
  };

  const handleBlur = (field) => {
    let error = '';
    
    switch (field) {
      case 'bank_name':
        error = validateBankName(form.bank_name);
        break;
      case 'branch_address':
        error = validateBranchAddress(form.branch_address);
        break;
      case 'ac_holder_name':
        error = validateAccountHolderName(form.ac_holder_name);
        break;
      case 'ac_number':
        error = validateAccountNumber(form.ac_number);
        break;
      case 'ifsc_code':
        error = validateIFSC(form.ifsc_code);
        break;
      default:
        break;
    }
    
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  /* =========================
     FETCH BANK DETAILS
  ========================= */
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!token) {
        setError('Session expired. Please login again.');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          'https://playinbyd.com/api/api-get-user-bank-details',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              app_key: 'SgRTXsywVmWteEBLlYWecwgbDiHwlh',
              env_type: 'Prod',
              unique_jwt_token: token
            })
          }
        );

        const data = await res.json();
        const bank = data?.bank_details;

        if (bank?.bank_name) {
          setForm({
            bank_name: bank.bank_name || '',
            branch_address: bank.branch_address || '',
            ac_holder_name: bank.ac_holder_name || '',
            ac_number: bank.ac_number || '',
            ifsc_code: bank.ifsc_code || ''
          });
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load bank details');
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [token]);

  /* =========================
     SUBMIT BANK DETAILS
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateAll()) {
      setError('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch(
        'https://playinbyd.com/api/api-add-user-bank-details',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            app_key: 'SgRTXsywVmWteEBLlYWecwgbDiHwlh',
            env_type: 'Prod',
            unique_jwt_token: token,
            ...form
          })
        }
      );

      const data = await res.json();

      if (data.success) {
        setSuccess('Bank details saved successfully');
      } else {
        setError(data.msg || 'Failed to save bank details');
      }
    } catch (err) {
      console.error(err);
      setError('Server error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bet-history-page">
      <div className="deposit-page">
        <div className="main-card">

          {/* HEADER */}
          <div className="main-card-header">
            <h1 className="page-title">Bank Details</h1>
            <p className="page-subtitle">
              Add or update your bank information for withdrawals
            </p>
          </div>

          {/* CONTENT */}
          {loading ? (
            <p className="page-subtitle">Loading bank details...</p>
          ) : (
            <form className="deposit-form" onSubmit={handleSubmit}>
              {/* BANK NAME */}
              <div className="form-group">
                <label>Bank Name *</label>
                <input
                  name="bank_name"
                  value={form.bank_name}
                  onChange={handleBankNameChange}
                  onBlur={() => handleBlur('bank_name')}
                  placeholder="Enter bank name (e.g., State Bank of India)"
                  className={errors.bank_name ? 'error-input' : ''}
                />
                {errors.bank_name && <div className="error">{errors.bank_name}</div>}
                <div className="hint">Letters, spaces, dots, hyphens and & only (max 100 chars)</div>
              </div>

              {/* BRANCH ADDRESS */}
              <div className="form-group">
                <label>Branch Address *</label>
                <input
                  name="branch_address"
                  value={form.branch_address}
                  onChange={handleBranchAddressChange}
                  onBlur={() => handleBlur('branch_address')}
                  placeholder="Enter full branch address"
                  className={errors.branch_address ? 'error-input' : ''}
                />
                {errors.branch_address && <div className="error">{errors.branch_address}</div>}
                <div className="hint">Max 200 characters, alphanumeric and common symbols</div>
              </div>

              {/* ACCOUNT HOLDER NAME */}
              <div className="form-group">
                <label>Account Holder Name *</label>
                <input
                  name="ac_holder_name"
                  value={form.ac_holder_name}
                  onChange={handleAccountHolderChange}
                  onBlur={() => handleBlur('ac_holder_name')}
                  placeholder="Enter account holder's full name"
                  className={errors.ac_holder_name ? 'error-input' : ''}
                />
                {errors.ac_holder_name && <div className="error">{errors.ac_holder_name}</div>}
                <div className="hint">Only letters, spaces, apostrophes, hyphens and dots (max 100 chars)</div>
              </div>

              {/* ACCOUNT NUMBER */}
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  name="ac_number"
                  value={form.ac_number}
                  onChange={handleAccountNumberChange}
                  onBlur={() => handleBlur('ac_number')}
                  placeholder="Enter account number (9-18 digits)"
                  className={errors.ac_number ? 'error-input' : ''}
                  type="text"
                  inputMode="numeric"
                  maxLength="18"
                />
                {errors.ac_number && <div className="error">{errors.ac_number}</div>}
                <div className="hint">9 to 18 digits only, no spaces or special characters</div>
              </div>

              {/* IFSC CODE */}
              <div className="form-group">
                <label>IFSC Code *</label>
                <input
                  name="ifsc_code"
                  value={form.ifsc_code}
                  onChange={handleIFSCChange}
                  onBlur={() => handleBlur('ifsc_code')}
                  placeholder="Enter 11-character IFSC (e.g., SBIN0000123)"
                  className={errors.ifsc_code ? 'error-input' : ''}
                  type="text"
                  maxLength="11"
                />
                {errors.ifsc_code && <div className="error">{errors.ifsc_code}</div>}
                <div className="hint">11 characters: 4 letters, then 0, then 6 alphanumeric</div>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Bank Details'}
              </button>

              {error && <div className="error">{error}</div>}
              {success && <div className="success">{success}</div>}
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default BankDetails;